import { BrandLogo, BrandIcon } from "@/components/Brand";

// Shared shell for public static pages (Terms, Privacy, and any future
// legal/marketing sub-page). Matches the visual language of Landing.jsx
// so the user never feels they've left the brand.

export function LegalPage({ title, updatedAt, children, testId }) {
    return (
        <div
            data-testid={testId || "legal-page-root"}
            className="min-h-[100dvh] bg-[var(--bg)] text-[var(--ink)] flex flex-col"
        >
            {/* Header (idéntico al de Landing) */}
            <header className="px-6 md:px-12 py-6 flex items-center justify-between">
                <a
                    href="/"
                    className="flex items-center gap-2.5 shrink-0"
                    data-testid="legal-brand"
                >
                    <BrandIcon
                        variant="dark"
                        style={{ height: 32, width: "auto" }}
                    />
                    <BrandLogo
                        variant="dark"
                        style={{ height: 16, width: "auto" }}
                        className="hidden sm:block"
                    />
                </a>
                <div className="flex items-center gap-2">
                    <a
                        href="/"
                        className="btn-ghost"
                        data-testid="legal-nav-home"
                    >
                        Volver al inicio
                    </a>
                </div>
            </header>

            {/* Contenido legal */}
            <main className="flex-1 px-6 md:px-12 py-8 md:py-16">
                <article className="max-w-[720px] mx-auto">
                    <div className="text-[11px] uppercase tracking-[0.22em] text-[var(--ink-soft)] mb-3">
                        Documento legal
                    </div>
                    <h1
                        data-testid="legal-title"
                        className="font-serif leading-[1.05]"
                        style={{ fontSize: "clamp(36px, 6vw, 56px)" }}
                    >
                        {title}
                    </h1>
                    {updatedAt && (
                        <p className="text-xs text-[var(--ink-soft)] mt-4">
                            Última actualización:{" "}
                            <time dateTime={updatedAt}>{updatedAt}</time>
                        </p>
                    )}

                    <div className="mt-10 legal-content space-y-8 text-[15px] leading-[1.75] text-[var(--ink)]">
                        {children}
                    </div>

                    <p className="mt-14 pt-8 border-t border-[var(--line)] text-xs text-[var(--ink-soft)]">
                        Este texto es provisional y se sustituirá por la
                        versión legal definitiva antes del lanzamiento
                        comercial. Si tienes dudas puedes escribirnos a{" "}
                        <a
                            href="mailto:hola@lovingangels.es"
                            className="underline underline-offset-2 hover:text-[var(--ink)]"
                        >
                            hola@lovingangels.es
                        </a>
                        .
                    </p>
                </article>
            </main>

            {/* Footer (mismo patrón que Landing) */}
            <footer className="px-6 md:px-12 py-10 border-t border-[var(--line)] text-center">
                <BrandLogo
                    variant="dark"
                    style={{ height: 22, width: "auto" }}
                    className="mx-auto"
                />
                <div className="text-xs text-[var(--ink-soft)] mt-3">
                    Hecho con cariño para parejas que celebran el amor en
                    todas sus formas.
                </div>
                <div className="mt-4 flex items-center justify-center gap-4 text-[11px] text-[var(--ink-soft)] uppercase tracking-[0.18em]">
                    <a
                        href="/terms"
                        className="hover:text-[var(--ink)] transition-colors"
                        data-testid="footer-terms-link"
                    >
                        Términos
                    </a>
                    <span aria-hidden="true">·</span>
                    <a
                        href="/privacy"
                        className="hover:text-[var(--ink)] transition-colors"
                        data-testid="footer-privacy-link"
                    >
                        Privacidad
                    </a>
                </div>
                <div className="text-[10px] text-[var(--ink-soft)] mt-4 tracking-[0.22em] uppercase">
                    © {new Date().getFullYear()} Loving Angels
                </div>
            </footer>
        </div>
    );
}

// Section helpers to keep individual pages readable + consistent typography.
export function LegalSection({ title, id, children }) {
    return (
        <section id={id} className="scroll-mt-24">
            <h2 className="font-serif text-2xl md:text-[28px] leading-tight mb-4">
                {title}
            </h2>
            <div className="space-y-4 text-[var(--ink)]/90">{children}</div>
        </section>
    );
}
